import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from "@/components/ui/tooltip";
import { CheckCircle2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { assignable, assigneeLabel } from "@/lib/assignees";
import { useLastPhoneCall, fmtDur } from "@/components/DesktopCallPrompt";

// outcome -> what happens to the prospect
export const OUTCOMES = [
  { value: "Interested", effect: "Opens lead conversion; status becomes Interested" },
  { value: "Call Back", effect: "Status Callback; a call-back task is created for the chosen time" },
  { value: "No Answer", effect: "Status No Answer; stays in queue for retry" },
  { value: "Busy", effect: "Status No Answer; stays in queue for retry" },
  { value: "Switched Off", effect: "Status No Answer; stays in queue for retry" },
  { value: "Not Interested", effect: "Status Not Interested; removed from queue" },
  { value: "Invalid Number", effect: "Status Invalid Number; removed from queue" },
  { value: "Wrong Number", effect: "Status Wrong Number; removed from queue" },
  { value: "Already Admitted", effect: "Status Already Admitted; removed from queue" },
  { value: "Not Eligible", effect: "Status Not Eligible; removed from queue" },
  { value: "Do Not Call", effect: "Status Do Not Call; never called again" },
];

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");
const iso = (v) => (v ? new Date(v).toISOString() : new Date().toISOString());

export function OutcomePanel({ prospect, calling, onSaved, onInterested, kind = "prospect" }) {
  const { config } = useAuth();
  const lead = kind === "lead";
  const opts = lead ? [...(config?.dispositions || []).map((d) => ({ value: d, effect: LEAD_EFFECT(d) })), { value: "Lost", effect: "Marks the lead Lost with the reason you pick" }] : OUTCOMES;
  const [outcome, setOutcome] = useState("");
  const [desc, setDesc] = useState("");
  const [cb, setCb] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const phoneCall = useLastPhoneCall(lead ? { lead_id: prospect?.id, student_id: prospect?.student_id } : { prospect_id: prospect?.id }, !!(calling && prospect?.id));
  useEffect(() => { setOutcome(""); setDesc(""); setCb(""); setReason(""); }, [prospect?.id]);
  const info = opts.find((o) => o.value === outcome);
  const isCb = /call ?back/i.test(outcome);
  const isLost = /^(lost|not interested)$/i.test(outcome);

  const save = async () => {
    if (!outcome) { toast.error("Select an outcome"); return; }
    if (isCb && !cb) { toast.error("Pick a callback date & time"); return; }
    if (outcome === "Lost" && !reason) { toast.error("Select the lost reason"); return; }
    setBusy(true);
    try {
      if (lead) {
        await api.post("/calls", { lead_id: prospect.id, student_id: prospect.student_id, disposition: outcome, notes: [reason && `Lost reason: ${reason}`, desc].filter(Boolean).join(" · "), duration: phoneCall?.duration || 0, direction: "Outbound" });
        if (outcome === "Lost") await api.patch(`/leads/${prospect.id}`, { stage: "Lost", lost_reason: reason });
        if (isCb) await api.post("/followups", { student_name: prospect.student_name, student_id: prospect.student_id, lead_id: prospect.id, type: "Call", due_at: iso(cb), owner_id: prospect.assigned_to || undefined, notes: desc || "Call back" });
        toast.success(`Outcome saved: ${outcome}`);
        onSaved?.({ last_disposition: outcome, last_call_at: new Date().toISOString(), ...(isCb ? { next_followup_at: iso(cb) } : {}), ...(outcome === "Lost" ? { stage: "Lost", lost_reason: reason } : {}) });
      } else {
        const { data } = await api.post(`/prospects/${prospect.id}/disposition`, {
          disposition: outcome, notes: desc, duration: phoneCall?.duration || 0, callback_at: cb ? iso(cb) : "", lost_reason: reason });
        toast.success(`Outcome saved: ${outcome} → ${data.status}`);
        if (outcome === "Interested") onInterested?.(data); else onSaved?.(data);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="space-y-3" data-testid="outcome-panel">
      {calling && <div className="text-xs rounded-lg bg-indigo-50 text-[#312E81] px-3 py-2" data-testid="outcome-duration">{phoneCall ? <>Call duration from phone: <b>{fmtDur(phoneCall.duration)}</b></> : "Call duration will appear here when the call ends on your phone…"}</div>}
      <div><Label className="text-xs">Outcome</Label>
        <Select value={outcome} onValueChange={setOutcome}>
          <SelectTrigger className="bg-white mt-1" data-testid="outcome-select"><SelectValue placeholder="Select outcome" /></SelectTrigger>
          <SelectContent className="bg-white">{opts.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`outcome-opt-${slug(o.value)}`}>{o.value}</SelectItem>)}</SelectContent>
        </Select>
        {info && <div className="text-[11px] text-[#4F46E5] mt-1" data-testid="outcome-effect">{info.effect}</div>}
      </div>
      {isCb && (
        <div><Label className="text-xs">Callback Date & Time</Label><Input type="datetime-local" value={cb} onChange={(e) => setCb(e.target.value)} className="mt-1" data-testid="outcome-callback" /></div>
      )}
      {isLost && (
        <div><Label className="text-xs">Lost Reason{outcome === "Lost" ? " *" : ""}</Label>
          <Select value={reason} onValueChange={setReason}><SelectTrigger className="bg-white mt-1" data-testid="outcome-lost-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
            <SelectContent className="bg-white">{(config?.lost_reasons || []).map((r) => <SelectItem key={r} value={r} data-testid={`lost-reason-opt-${slug(r)}`}>{r}</SelectItem>)}</SelectContent></Select>
        </div>
      )}
      <div><Label className="text-xs">Description</Label>
        <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} placeholder="What did the student say?" className="mt-1" data-testid="outcome-description" />
      </div>
      <Button onClick={save} disabled={busy} className="w-full bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="outcome-save-btn">Save Outcome</Button>
    </div>
  );
}

export function PField({ k, field, p, v }) {
  const val = v !== undefined ? v : p?.[field];
  const ed = field && p?.edited_fields?.[field];
  return (
    <div data-testid={`pfield-${field || slug(k)}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center gap-1">{k}
        {ed && (
          <TooltipProvider delayDuration={100}><Tooltip><TooltipTrigger asChild>
            <span className="px-1 rounded bg-amber-100 text-amber-700 normal-case tracking-normal text-[9px] font-semibold cursor-help" data-testid={`edited-badge-${field}`}>Edited</span>
          </TooltipTrigger><TooltipContent className="bg-[#1E1B4B] text-white border-0 text-xs max-w-xs">
            Was: {String(ed.old || "—")}<br />Now: {String(ed.new || "—")}<br />{ed.by} · {fmtDateTime(ed.at)}
          </TooltipContent></Tooltip></TooltipProvider>
        )}
      </div>
      <div className={`font-medium truncate ${ed ? "text-amber-800" : "text-slate-700"}`}>{val || val === 0 ? val : "—"}</div>
    </div>
  );
}

const EDIT_FIELDS = [["name", "Name", 2], ["mobile", "Mobile"], ["alt_mobile", "Alt Mobile"], ["email", "Email", 2],
  ["city", "City"], ["state", "State"], ["language", "Language"], ["_acad", "Academic Details", 2], ["course_interest", "Interested Course"], ["exam", "Exam"],
  ["rank", "Rank"], ["year", "Year"], ["twelfth_pct", "12th %"], ["stream", "12th Stream"], ["course_pursued", "Course Pursued (for PG)"], ["ug_aggregate", "Aggregate % / CGPA (for PG)"]];

const LEAD_EDIT_FIELDS = [["student_name", "Name", 2], ["mobile", "Mobile"], ["alt_mobile", "Alt Mobile"], ["email", "Email", 2],
  ["city", "City"], ["state", "State"], ["language", "Language"], ["_acad", "Academic Details", 2], ["course", "Interested Course"], ["exam", "Exam"],
  ["rank", "Rank"], ["twelfth_pct", "12th %"], ["stream", "12th Stream"], ["course_pursued", "Course Pursued (for PG)"], ["ug_aggregate", "Aggregate % / CGPA (for PG)", 2]];

const LEAD_EFFECT = (d) => (/call ?back/i.test(d) ? "A call-back follow-up is created for the chosen time" : /admission confirmed/i.test(d) ? "Logs the call as admission confirmed" : /invalid|wrong|not interested/i.test(d) ? "Logs the call; consider marking the lead Lost" : "Logs the call against this lead");

/** "More Details" (imported / Facebook form answers) for the queue side panel. */
export function MoreDetailsCard({ extra }) {
  const [open, setOpen] = useState(false);
  const ex = Object.entries(extra || {}).filter(([, v]) => String(v ?? "").trim());
  if (!ex.length) return null;
  return (
    <div className="rounded-xl border-l-4 border-rose-400 bg-rose-50/60 p-3 shadow-sm text-rose-700" data-testid="queue-more-details">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between text-[10px] font-bold uppercase tracking-wider" data-testid="queue-more-details-toggle">
        <span>More Details ({ex.length})</span><span className="text-xs">{open ? "▲ Hide" : "▼ Show"}</span>
      </button>
      {open && <div className="space-y-1.5 text-slate-800 mt-2">{ex.map(([k, v]) => (
        <div key={k}><div className="text-[10px] uppercase tracking-wider text-slate-400">{k.replace(/_/g, " ")}</div><div className="font-medium break-words" data-testid={`queue-extra-${k}`}>{String(v)}</div></div>
      ))}</div>}
    </div>
  );
}

const TITLES = { edit: "Edit Details", task: "Add Task", fu: "Schedule Follow-up", assign: "Change Assignee", note: "Add Note", wa: "Send WhatsApp", sms: "Send SMS" };

function initFor(mode, p) {
  if (mode === "edit") return { ...p, rank: p.rank || "", tag: p.tag || (p.tags || [])[0] || "" };
  if (mode === "assign") return { assigned_to: p.assigned_to || "" };
  if (mode === "task") return { type: "Call", priority: "Normal", due_at: "", title: "" };
  if (mode === "wa" || mode === "sms") return { text: `Hi ${p.name || p.student_name}, this is OhCampus.com regarding your ${p.course_interest || p.course || "admission"} enquiry.` };
  return { type: "Call", due_at: "", notes: "", text: "" };
}

async function leadSubmit(mode, p, mf, user) {
  const name = p.student_name;
  if (mode === "edit") {
    const body = { tags: mf.tag ? [mf.tag] : [] }; LEAD_EDIT_FIELDS.filter(([k]) => !k.startsWith("_")).forEach(([k]) => { body[k] = k === "rank" ? Number(mf.rank) || 0 : (mf[k] || ""); });
    const { data } = await api.patch(`/leads/${p.id}`, body);
    if (p.student_id) {
      const sp = { name: body.student_name, mobile: body.mobile, alt_mobile: body.alt_mobile, email: body.email, city: body.city, state: body.state, language: body.language, exam: body.exam, twelfth_pct: body.twelfth_pct, stream: body.stream, course_pursued: body.course_pursued, ug_aggregate: body.ug_aggregate };
      if (String(mf.rank ?? "").trim()) sp.rank = body.rank;
      try { await api.patch(`/students/${p.student_id}`, sp); } catch (e) { toast.warning(`Lead saved, but academic details were not saved: ${formatApiError(e.response?.data?.detail)}`); }
    }
    toast.success("Details updated"); return { ...data, twelfth_pct: body.twelfth_pct, course_pursued: body.course_pursued, ug_aggregate: body.ug_aggregate };
  }
  if (mode === "assign") {
    if (!mf.assigned_to) throw new Error("Select an assignee");
    const { data } = await api.patch(`/leads/${p.id}`, { assigned_to: mf.assigned_to }); toast.success("Assignee updated"); return data;
  }
  if (mode === "task") {
    if (!mf.title?.trim()) throw new Error("Enter a task title");
    await api.post("/tasks", { student_name: name, student_id: p.student_id, lead_id: p.id, type: mf.type, title: mf.title, priority: mf.priority, due_at: iso(mf.due_at), owner_id: p.assigned_to || user.id });
    toast.success("Task added"); return null;
  }
  if (mode === "fu") {
    if (!mf.due_at) throw new Error("Pick a follow-up date & time");
    await api.post("/followups", { student_name: name, student_id: p.student_id, lead_id: p.id, type: mf.type || "Call", due_at: iso(mf.due_at), owner_id: p.assigned_to || user.id, notes: mf.notes || "", important: !!mf.important });
    toast.success("Follow-up scheduled"); return { id: p.id, next_followup_at: iso(mf.due_at) };
  }
  if (mode === "note") {
    if (!mf.text?.trim()) throw new Error("Write a note");
    await api.post(`/leads/${p.id}/remarks`, { text: mf.text }); toast.success("Note saved"); return null;
  }
  if (!mf.text?.trim()) throw new Error("Write a message");
  const enc = encodeURIComponent(mf.text);
  try { window.open(mode === "wa" ? `https://wa.me/91${p.mobile}?text=${enc}` : `sms:${p.mobile}?body=${enc}`, "_blank"); } catch { /* ignore */ }
  toast.success(`Opening ${mode === "wa" ? "WhatsApp" : "SMS"} on your device`); return null;
}

export function ProspectDialog({ mode, prospect: p, onClose, onSaved, kind = "prospect" }) {
  const { config, user } = useAuth();
  const [mf, setMf] = useState({});
  const [users, setUsers] = useState([]);
  useEffect(() => { if (mode && p) setMf(initFor(mode, p)); }, [mode, p?.id]); // eslint-disable-line
  useEffect(() => {
    if (mode === "assign") api.get("/users").then(({ data }) => setUsers(assignable(data))).catch(() => {});
  }, [mode]);
  const set = (k) => (e) => setMf({ ...mf, [k]: e.target ? e.target.value : e });

  const submit = async () => {
    if (kind === "lead") {
      try { const d = await leadSubmit(mode, p, mf, user); onSaved?.(d); onClose(); } catch (e) { toast.error(e.response ? formatApiError(e.response?.data?.detail) : e.message); }
      return;
    }
    try {
      let data = null;
      if (mode === "edit") {
        const body = { tag: mf.tag || "" }; EDIT_FIELDS.filter(([k]) => !k.startsWith("_")).forEach(([k]) => { body[k] = k === "rank" ? Number(mf.rank) || 0 : (mf[k] || ""); });
        data = (await api.patch(`/prospects/${p.id}`, body)).data; toast.success("Details updated");
      } else if (mode === "assign") {
        if (!mf.assigned_to) { toast.error("Select an assignee"); return; }
        data = (await api.patch(`/prospects/${p.id}`, { assigned_to: mf.assigned_to })).data; toast.success("Assignee updated");
      } else if (mode === "task") {
        if (!mf.title?.trim()) { toast.error("Enter a task title"); return; }
        await api.post("/tasks", { student_name: p.name, prospect_id: p.id, type: mf.type, title: mf.title, priority: mf.priority, due_at: iso(mf.due_at), owner_id: p.assigned_to || user.id });
        toast.success("Task added");
      } else if (mode === "fu") {
        if (!mf.due_at) { toast.error("Pick a follow-up date & time"); return; }
        await api.post("/followups", { student_name: p.name, prospect_id: p.id, type: mf.type || "Call", due_at: iso(mf.due_at), owner_id: p.assigned_to || user.id, notes: mf.notes || "", important: !!mf.important });
        data = { id: p.id, next_action_at: iso(mf.due_at) }; toast.success("Follow-up scheduled");
      } else if (mode === "note") {
        if (!mf.text?.trim()) { toast.error("Write a note"); return; }
        await api.post(`/prospects/${p.id}/note`, { text: mf.text }); toast.success("Note saved");
      } else if (mode === "wa" || mode === "sms") {
        if (!mf.text?.trim()) { toast.error("Write a message"); return; }
        await api.post(`/prospects/${p.id}/message`, { channel: mode === "wa" ? "WhatsApp" : "SMS", text: mf.text });
        const enc = encodeURIComponent(mf.text);
        try { window.open(mode === "wa" ? `https://wa.me/91${p.mobile}?text=${enc}` : `sms:${p.mobile}?body=${enc}`, "_blank"); } catch { /* ignore */ }
        toast.success(`${mode === "wa" ? "WhatsApp" : "SMS"} logged (sending is MOCKED — opens your device app)`);
      }
      onSaved?.(data); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={!!mode} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{TITLES[mode]}</DialogTitle></DialogHeader>
        {mode === "edit" && (
          <div className="grid grid-cols-2 gap-3">
            {(kind === "lead" ? LEAD_EDIT_FIELDS : EDIT_FIELDS).map(([k, l, span]) => k.startsWith("_") ? (
              <div key={k} className="col-span-2 text-xs font-bold uppercase tracking-wider text-emerald-700 border-t border-slate-100 pt-2" data-testid="pedit-academic-heading">{l}</div>
            ) : (
              <div key={k} className={span === 2 ? "col-span-2" : ""}><Label>{l}{p?.edited_fields?.[k] && <span className="ml-1 text-[10px] text-amber-600">(edited)</span>}</Label>
                <Input type={k === "rank" ? "number" : "text"} value={mf[k] ?? ""} onChange={set(k)} data-testid={`pedit-${k}`} /></div>
            ))}
            {(
              <div className="col-span-2"><Label>Tag (optional, pick one)</Label>
              <div className="flex flex-wrap gap-1.5 mt-1" data-testid="pedit-tag">
                {(config?.prospect_tags || []).map((t) => (
                  <button key={t} type="button" onClick={() => setMf({ ...mf, tag: mf.tag === t ? "" : t })} data-testid={`pedit-tag-${t.replace(/\s+/g, "-").toLowerCase()}`}
                    className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${mf.tag === t ? "bg-amber-400 border-amber-400 text-[#312E81] font-semibold" : "bg-white border-slate-200 text-slate-600 hover:border-amber-300"}`}>{t}</button>
                ))}
              </div>
            </div>)}
          </div>
        )}
        {mode === "task" && (
          <div className="space-y-3">
            <div><Label>Title</Label><Input value={mf.title || ""} onChange={set("title")} placeholder="e.g. Send brochure" data-testid="ptask-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Type</Label><Select value={mf.type} onValueChange={set("type")}><SelectTrigger data-testid="ptask-type"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{(config?.followup_types || ["Call", "WhatsApp", "Email"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Priority</Label><Select value={mf.priority} onValueChange={set("priority")}><SelectTrigger data-testid="ptask-priority"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{(config?.priorities || ["High", "Normal", "Low"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label>Due</Label><Input type="datetime-local" value={mf.due_at || ""} onChange={set("due_at")} data-testid="ptask-due" /></div>
          </div>
        )}
        {mode === "fu" && (
          <div className="space-y-3">
            <div><Label>Type</Label><Select value={mf.type || "Call"} onValueChange={set("type")}><SelectTrigger data-testid="pfu-type"><SelectValue /></SelectTrigger><SelectContent className="bg-white">{(config?.followup_types || ["Call", "WhatsApp", "Email"]).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Follow-up Date/Time</Label><Input type="datetime-local" value={mf.due_at || ""} onChange={set("due_at")} data-testid="pfu-due" /></div>
            <div><Label>Notes</Label><Textarea value={mf.notes || ""} onChange={set("notes")} rows={2} data-testid="pfu-notes" /></div>
            <label className="flex items-center gap-2 text-sm text-amber-700"><input type="checkbox" className="accent-amber-500" checked={!!mf.important} onChange={(e) => setMf({ ...mf, important: e.target.checked })} data-testid="pfu-important" /> Important — pop up a reminder at the due time</label>
          </div>
        )}
        {mode === "assign" && (
          <div><Label>Assign To</Label>
            <Select value={mf.assigned_to || ""} onValueChange={set("assigned_to")}>
              <SelectTrigger data-testid="passign-select"><SelectValue placeholder="Select counselor / telecaller" /></SelectTrigger>
              <SelectContent className="bg-white">{users.map((u) => <SelectItem key={u.id} value={u.id}>{assigneeLabel(u)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        {["note", "wa", "sms"].includes(mode) && (
          <div><Label>{mode === "note" ? "Note" : "Message"}</Label><Textarea value={mf.text || ""} onChange={set("text")} rows={4} data-testid={`p${mode}-text`} /></div>
        )}
        <DialogFooter><Button onClick={submit} className="bg-[#4F46E5] hover:bg-[#4338CA]" data-testid="pdialog-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ConvertDialog({ prospect, open, onOpenChange, onDone }) {
  const { config } = useAuth();
  const [form, setForm] = useState({ course: "", rank: "", budget: "", priority: "High" });
  useEffect(() => { if (open) setForm({ course: prospect?.course_interest || "", rank: prospect?.rank || "", budget: "", priority: "High" }); }, [open]); // eslint-disable-line
  const convert = async () => {
    try {
      const { data } = await api.post(`/prospects/${prospect.id}/convert`, { ...form, rank: Number(form.rank) || 0, budget: Number(form.budget) || 0, timeline: new Date().toISOString() });
      toast.success(`Converted to Lead ${data.code}`); onOpenChange(false); onDone?.(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const courses = ["MBBS", "BDS", "B.Pharm", "B.E. Computer Science", "MBA", "MCA", "B.Sc Nursing", "BCA"];
  if (form.course && !courses.includes(form.course)) courses.unshift(form.course);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><UserPlus className="w-5 h-5 text-[#4F46E5]" /> Convert Prospect to Lead</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500 -mt-2">Quick qualification for <span className="font-semibold">{prospect?.name}</span>. History & campaign are preserved.</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Interested Course</Label>
            <Select value={form.course} onValueChange={(v) => setForm({ ...form, course: v })}>
              <SelectTrigger data-testid="convert-course"><SelectValue placeholder="Select course" /></SelectTrigger>
              <SelectContent className="bg-white">{courses.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Rank / Score</Label><Input type="number" value={form.rank} onChange={(e) => setForm({ ...form, rank: e.target.value })} data-testid="convert-rank" /></div>
          <div><Label>Budget (₹)</Label><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })} data-testid="convert-budget" /></div>
          <div className="col-span-2"><Label>Priority</Label>
            <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
              <SelectTrigger data-testid="convert-priority"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{config?.priorities?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={convert} className="bg-[#312E81]" data-testid="confirm-convert-btn"><CheckCircle2 className="w-4 h-4 mr-1" /> Create Lead</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ActivityList({ timeline }) {
  const items = [
    ...(timeline.notes || []).map((n) => ({ k: `n${n.at}`, c: "border-amber-300", t: `Note: ${n.text}`, s: `${n.by} · ${fmtDateTime(n.at)}`, at: n.at })),
    ...(timeline.calls || []).map((c) => ({ k: c.id, c: "border-indigo-300", t: `Call: ${c.disposition}${c.notes ? ` — ${c.notes}` : ""}`, s: `${c.counselor_name} · ${fmtDateTime(c.created_at)}`, at: c.created_at })),
    ...(timeline.activities || []).map((a) => ({ k: a.id, c: "border-slate-200", t: `${a.action}${a.detail && a.action.startsWith("edited") ? ` (${a.detail})` : ""}`, s: `${a.actor_name} · ${fmtDateTime(a.created_at)}`, at: a.created_at })),
  ].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  if (!items.length) return <div className="text-xs text-slate-400">No activity yet</div>;
  return items.map((i) => (
    <div key={i.k} className={`text-xs border-l-2 ${i.c} pl-2`}><div className="text-slate-700">{i.t}</div><div className="text-[10px] text-slate-400">{i.s}</div></div>
  ));
}
